import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarIcon, DollarSignIcon, CheckIcon, XIcon, CircleIcon, ShieldIcon, ClockIcon } from 'lucide-react';
import { getProducts, getBookingSlots, getReservedDates } from '../services/api';
import { Product, BookingSlot } from '../types';
import { format, addDays, isBefore, isAfter } from 'date-fns';
import ImageCarousel from '../components/ImageCarousel';
import DateRangePicker from '../components/DateRangePicker';
import { saveDatesToStorage, loadDatesFromStorage, clearDatesFromStorage } from '../utils/dateStorage';
import { getContent } from '../config/content';
import { theme } from '../config/theme';

const Rentals: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedStartDate, setSelectedStartDate] = useState<string>('');
  const [selectedEndDate, setSelectedEndDate] = useState<string>('');
  const [availability, setAvailability] = useState<{ [key: string]: boolean }>({});
  const [dateAvailability, setDateAvailability] = useState<{ [date: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
    // Load saved dates on component mount
    const savedDates = loadDatesFromStorage();
    const today = format(new Date(), 'yyyy-MM-dd');
    if (savedDates.startDate && savedDates.startDate < today) {
      // Saved date is in the past — drop it so it doesn't pre-fill the picker
      clearDatesFromStorage();
    } else if (savedDates.startDate && savedDates.endDate) {
      setSelectedStartDate(savedDates.startDate);
      setSelectedEndDate(savedDates.endDate);
    }
  }, []);

  useEffect(() => {
    if (selectedStartDate && selectedEndDate && products.length > 0) {
      checkAvailability();
      // Save dates to localStorage whenever they change
      saveDatesToStorage(selectedStartDate, selectedEndDate);
    } else if (selectedStartDate && products.length > 0) {
      // If only start date is selected, still check availability for the calendar
      checkAvailabilityForCalendar();
    } else {
      setAvailability({});
      setDateAvailability({});
    }
  }, [selectedStartDate, selectedEndDate, products]);

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const data = await getProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const checkAvailabilityForCalendar = async () => {
    if (!selectedStartDate || products.length === 0) return;
    
    try {
      // Get reserved dates for the full year using the fast reserved dates API
      const startDateObj = new Date(selectedStartDate);
      const endDateObj = new Date(selectedStartDate);
      endDateObj.setDate(endDateObj.getDate() + 365); // Check 1 year ahead
      
      const startDateStr = format(startDateObj, 'yyyy-MM-dd');
      const endDateStr = format(endDateObj, 'yyyy-MM-dd');
      
      // Fetch reserved dates for all products (no product IDs filter)
      const reservedDatesData = await getReservedDates(startDateStr, endDateStr);
      
      // Convert reserved dates to availability map
      // Dates are reserved if they appear in the reserved dates data for any product
      const dateAvailMap: { [date: string]: boolean } = {};
      
      // Initialize all dates as available (true)
      const currentDate = new Date(startDateObj);
      while (currentDate <= endDateObj) {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        dateAvailMap[dateStr] = true; // Assume available initially
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Mark reserved dates as unavailable (false)
      (Object.entries(reservedDatesData) as [string, string[]][]).forEach(([_, reservedDates]) => {
        reservedDates.forEach((reservedDate: string) => {
          dateAvailMap[reservedDate] = false; // Mark as unavailable
        });
      });
      
      setDateAvailability(dateAvailMap);
    } catch (error) {
      console.error('Error checking reserved dates for calendar:', error);
    }
  };

  const checkAvailability = async () => {
    if (!selectedStartDate || !selectedEndDate) {
      setAvailability({});
      setDateAvailability({});
      setLoading(false);
      return;
    }

    setLoading(true);
    const availabilityMap: { [key: string]: boolean } = {};
    const dateAvailMap: { [date: string]: boolean } = {};

    try {
      // Use UTC date formatting to prevent timezone shifting
      const startDateStr = new Date(selectedStartDate + 'T00:00:00Z').toISOString().split('T')[0];
      const endDateStr = new Date(selectedEndDate + 'T00:00:00Z').toISOString().split('T')[0];
      
      // Use reserved dates API for fast availability checking (all products)
      const reservedDatesData = await getReservedDates(startDateStr, endDateStr);
      
      // Initialize date availability map
      const dateRange: string[] = [];
      const start = new Date(startDateStr + 'T00:00:00Z');
      const end = new Date(endDateStr + 'T00:00:00Z');
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        dateRange.push(dateStr);
        dateAvailMap[dateStr] = true; // Default to available
      }
      
      // Mark reserved dates as unavailable
      (Object.entries(reservedDatesData) as [string, string[]][]).forEach(([_, reservedDates]) => {
        reservedDates.forEach((reservedDate: string) => {
          dateAvailMap[reservedDate] = false; // Mark as unavailable
        });
      });
      
      // Check availability for each product based on conflicts
      for (const product of products) {
        const productReservedDates = reservedDatesData[product.product_id] || [];
        const hasReservedDateInRange = productReservedDates.some((reservedDate: string) => {
          return dateRange.includes(reservedDate);
        });
        
        // Product is available if no dates in the range are reserved
        availabilityMap[product.product_id] = !hasReservedDateInRange;
      }
      
      setAvailability(availabilityMap);
      setDateAvailability(dateAvailMap);
    } catch (error) {
      console.error('Error checking availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const getToday = () => format(new Date(), 'yyyy-MM-dd');
  const getMaxDate = () => format(addDays(new Date(), 365), 'yyyy-MM-dd');

  const handleStartDateChange = (date: string) => {
    setSelectedStartDate(date);
    if (date && selectedEndDate && isBefore(new Date(selectedEndDate), new Date(date))) {
      setSelectedEndDate('');
      clearDatesFromStorage();
    }
  };

  const handleEndDateChange = (date: string) => {
    if (date && isBefore(new Date(date), new Date(selectedStartDate))) {
      return;
    }
    setSelectedEndDate(date);
  };

  return (
    <div className={`min-h-screen ${theme.colors.background.secondary} py-8`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className={`text-4xl font-bold ${theme.colors.text.primary} mb-4`}>{getContent('rentals.title')}</h1>
          <p className="text-lg text-gray-600">
            {getContent('rentals.subtitle')}
          </p>
        </div>

        {/* Date Selection */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{getContent('rentals.dateSelection.title')}</h2>
          <DateRangePicker
            startDate={selectedStartDate}
            endDate={selectedEndDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            minDate={getToday()}
            maxDays={7}
            disableToday={true}
          />
          
          {selectedStartDate && selectedEndDate && (
            <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#dbeafe' }}>
              <p className="text-sm" style={{ color: '#1e40af' }}>
                <CalendarIcon className="inline h-4 w-4 mr-1" />
                Rental period: {format(new Date(selectedStartDate + 'T00:00:00'), 'MMM d, yyyy')} - {format(new Date(selectedEndDate + 'T00:00:00'), 'MMM d, yyyy')}
              </p>
            </div>
          )}
        </div>

        {/* Products Grid */}
        {productsLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: theme.colors.primary[600] }}></div>
            <p className="mt-2 text-gray-600">Loading rentals...</p>
          </div>
        ) : loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: theme.colors.primary[600] }}></div>
            <p className="mt-2 text-gray-600">{getContent('rentals.loading.checkingAvailability')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {Array.isArray(products) && products.map((product) => {
              const isAvailable = selectedStartDate && selectedEndDate ? 
                availability[product.product_id] === true : false;
              
              return (
                <div key={product.product_id} className={`bg-white rounded-lg shadow-md overflow-hidden flex flex-col`}>
                  {product.images && product.images.length > 0 ? (
                    <div className="flex-shrink-0">
                      <ImageCarousel
                        images={product.images}
                        alt={product.name}
                        className="h-56 sm:h-64 md:h-72 lg:h-80 xl:h-96"
                      />
                    </div>
                  ) : (
                    <div className="flex-shrink-0 h-56 sm:h-64 md:h-72 lg:h-80 xl:h-96 bg-gray-200 flex items-center justify-center">
                      <div className="text-gray-400 text-center">
                        <div className="w-12 h-12 mx-auto mb-2 bg-gray-300 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <p className="text-sm">No image</p>
                      </div>
                    </div>
                  )}
                  
                  <div 
                    className="p-4 sm:p-6 cursor-pointer transition-transform hover:scale-105 flex-grow flex flex-col justify-center"
                    onClick={() => navigate('/product', { state: { productId: product.product_id } })}
                  >
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                      {product.name}
                    </h3>
                    
                    <p className="text-gray-600 mb-3 sm:mb-4 line-clamp-2 text-sm sm:text-base whitespace-pre-wrap">
                      {product.description}
                    </p>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
                      <div className="flex items-center" style={{ color: theme.colors.primary[700] }}>
                        <span className="font-semibold text-sm sm:text-base">
                          ${product.price ? (product.price.amount_cents / 100) : 0}/day
                        </span>
                      </div>
                      
                      <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                        !selectedStartDate || !selectedEndDate
                          ? 'bg-gray-100 text-gray-700 border border-gray-200'
                          : isAvailable
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                        {!selectedStartDate || !selectedEndDate ? (
                          getContent('rentals.productCard.selectDates')
                        ) : isAvailable ? (
                          <>
                            <CheckIcon className="h-3 w-3 mr-1" />
                            Available
                          </>
                        ) : (
                          <>
                            <XIcon className="h-3 w-3 mr-1" />
                            {getContent('rentals.productCard.notAvailable')}
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-500 text-center">
                      {selectedStartDate && selectedEndDate
                        ? isAvailable
                          ? getContent('rentals.productCard.viewDetails')
                          : getContent('rentals.productCard.notAvailable')
                        : getContent('rentals.productCard.selectDates')
                      }
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {!productsLoading && !loading && products.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">{getContent('rentals.loading.noProducts')}</p>
          </div>
        )}

        {/* Additional Information Section */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Important Information</h3>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3 p-4 rounded-lg" style={{ backgroundColor: theme.colors.primary[50] }}>
              <ClockIcon className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: theme.colors.primary[600] }} />
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Standard Rental Times</h4>
                <p className="text-sm text-gray-600">
                  Standard rental times are 9:00 AM - 6:00 PM. We're flexible with timing - add the pick up next day addon to extend your rental time or add your preferred schedule to the booking notes and we'll coordinate with you.
                </p>
              </div>
            </div>
         
            <div className="flex items-start space-x-3 p-4 rounded-lg" style={{ backgroundColor: theme.colors.primary[50] }}>
              <ShieldIcon className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: theme.colors.primary[600] }} />
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Fully Insured</h4>
                <p className="text-sm text-gray-600">
                  We are fully insured up to $300,000 for your peace of mind and protection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Rentals;
